import { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { toast } from 'sonner';
import { 
  TrendingUp, BarChart3, Briefcase, Settings, LogOut, Brain, HelpCircle,
  Users, Search, Trash2, Mail, Edit, Shield, DollarSign, CheckCircle2, Save, Loader2,
  Ticket, Bug, Lightbulb, Clock, AlertCircle, MessageSquare, Image, Eye, Tag, Plus, RefreshCw, GraduationCap, Rocket, Sparkles, AlertTriangle
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';

import API from '../utils/api';

const AdminDashboardPage = () => {
  const { language } = useLanguage();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  
  // Users state
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [planFilter, setPlanFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  
  // System Prompt state
  const [systemPrompt, setSystemPrompt] = useState('');
  const [promptLoading, setPromptLoading] = useState(false);
  const [promptSaved, setPromptSaved] = useState(false);
  
  // Skill Score Prompt state
  const [skillScorePrompt, setSkillScorePrompt] = useState('');
  const [skillPromptLoading, setSkillPromptLoading] = useState(false);
  const [skillPromptSaved, setSkillPromptSaved] = useState(false);
  
  // Pricing state
  const [pricing, setPricing] = useState({
    basic: { price: 8.99, discount_active: false, discount_percent: 0, yearly_discount_percent: 0, promo_codes: [] },
    pro: { price: 14.99, discount_active: false, discount_percent: 0, yearly_discount_percent: 0, promo_codes: [] },
    premium: { price: 25.99, discount_active: false, discount_percent: 0, yearly_discount_percent: 0, promo_codes: [], lifetime_promo_codes: [] }
  });
  const [pricingLoading, setPricingLoading] = useState(false);
  const [pricingSaved, setPricingSaved] = useState(false);
  
  // Tickets state
  const [tickets, setTickets] = useState([]);
  const [ticketStats, setTicketStats] = useState(null);
  const [ticketFilter, setTicketFilter] = useState({ status: 'all', category: 'all', plan: 'all' });
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [adminComment, setAdminComment] = useState('');
  const [statusNote, setStatusNote] = useState('');
  const [pendingStatus, setPendingStatus] = useState(null); // New: pending status change
  const [savingTicket, setSavingTicket] = useState(false); // New: saving state
  
  // Modals
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newPlan, setNewPlan] = useState('');

  // Fetch users
  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/admin/users?plan=${planFilter}`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
        setFilteredUsers(data);
      } else if (response.status === 403) {
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  }, [planFilter, navigate]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const response = await fetch(`${API}/admin/stats`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, []);

  // Fetch system prompt
  const fetchSystemPrompt = useCallback(async () => {
    setPromptLoading(true);
    try {
      const response = await fetch(`${API}/admin/system-prompt`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setSystemPrompt(data.prompt);
      }
    } catch (error) {
      console.error('Error fetching system prompt:', error);
    } finally {
      setPromptLoading(false);
    }
  }, []);

  // Fetch skill score prompt
  const fetchSkillScorePrompt = useCallback(async () => {
    setSkillPromptLoading(true);
    try {
      const response = await fetch(`${API}/admin/skill-score-prompt`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setSkillScorePrompt(data.prompt);
      }
    } catch (error) {
      console.error('Error fetching skill score prompt:', error);
    } finally {
      setSkillPromptLoading(false);
    }
  }, []);

  // Fetch pricing
  const fetchPricing = useCallback(async () => {
    setPricingLoading(true);
    try {
      const response = await fetch(`${API}/admin/pricing`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        // Migrate old single promo_code format to new promo_codes array format
        for (const plan of ['basic', 'pro', 'premium']) {
          if (data[plan]) {
            if (!data[plan].promo_codes) {
              data[plan].promo_codes = [];
              if (data[plan].promo_code) {
                data[plan].promo_codes.push({
                  code: data[plan].promo_code,
                  discount_percent: data[plan].promo_discount_percent || 0,
                  expires: data[plan].promo_expires || '',
                  active: data[plan].promo_code_active || false
                });
              }
            }
            if (plan === 'premium' && !data[plan].lifetime_promo_codes) {
              data[plan].lifetime_promo_codes = [];
              if (data[plan].lifetime_promo_code) {
                data[plan].lifetime_promo_codes.push({
                  code: data[plan].lifetime_promo_code,
                  discount_percent: data[plan].lifetime_promo_discount_percent || 0,
                  expires: data[plan].lifetime_promo_expires || '',
                  active: data[plan].lifetime_promo_active || false
                });
              }
            }
          }
        }
        setPricing(data);
      }
    } catch (error) {
      console.error('Error fetching pricing:', error);
    } finally {
      setPricingLoading(false);
    }
  }, []);

  // Fetch tickets
  const fetchTickets = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (ticketFilter.status !== 'all') params.append('status', ticketFilter.status);
      if (ticketFilter.category !== 'all') params.append('category', ticketFilter.category);
      if (ticketFilter.plan !== 'all') params.append('plan', ticketFilter.plan);
      
      const response = await fetch(`${API}/admin/tickets?${params}`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setTickets(data);
      }
    } catch (error) {
      console.error('Error fetching tickets:', error);
    }
  }, [ticketFilter]);

  // Fetch ticket stats
  const fetchTicketStats = useCallback(async () => {
    try {
      const response = await fetch(`${API}/admin/tickets/stats`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setTicketStats(data);
      }
    } catch (error) {
      console.error('Error fetching ticket stats:', error);
    }
  }, []);

  useEffect(() => {
    if (!user?.is_admin) {
      navigate('/dashboard');
      return;
    }
    fetchUsers();
    fetchStats();
    fetchSystemPrompt();
    fetchSkillScorePrompt();
    fetchPricing();
    fetchTickets();
    fetchTicketStats();
  }, [user, fetchUsers, fetchStats, fetchSystemPrompt, fetchSkillScorePrompt, fetchPricing, fetchTickets, fetchTicketStats, navigate]);

  // Search filter
  useEffect(() => {
    if (searchTerm) {
      const filtered = users.filter(u => 
        u.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredUsers(filtered);
    } else {
      setFilteredUsers(users);
    }
  }, [searchTerm, users]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleResetPassword = async () => {
    try {
      const response = await fetch(`${API}/admin/reset-password/${selectedUser.user_id}`, {
        method: 'POST',
        credentials: 'include',
      });
      if (response.ok) {
        toast.success(
          language === 'de' ? 'E-Mail gesendet!' : 'Email sent!',
          {
            description: language === 'de' 
              ? `Reset-Link an ${selectedUser.email} gesendet` 
              : `Reset link sent to ${selectedUser.email}`,
            duration: 5000,
          }
        );
        setShowResetModal(false);
      }
    } catch (error) {
      console.error('Error resetting password:', error);
      toast.error(language === 'de' ? 'Fehler beim Senden' : 'Failed to send');
    }
  };

  const handleDeleteUser = async () => {
    try {
      const response = await fetch(`${API}/admin/user/${selectedUser.user_id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (response.ok) {
        setShowDeleteModal(false);
        fetchUsers();
        fetchStats();
      }
    } catch (error) {
      console.error('Error deleting user:', error);
    }
  };

  const handleUpdateSubscription = async () => {
    try {
      const response = await fetch(`${API}/admin/user/${selectedUser.user_id}/subscription`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ plan: newPlan }),
      });
      if (response.ok) {
        setShowEditModal(false);
        fetchUsers();
        fetchStats();
      }
    } catch (error) {
      console.error('Error updating subscription:', error);
    }
  };

  const [stripeSyncing, setStripeSyncing] = useState(false);
  const [cleanupRunning, setCleanupRunning] = useState(false);
  const [migrationAccountId, setMigrationAccountId] = useState('');
  const [migrating, setMigrating] = useState(false);
  const [showMigrateModal, setShowMigrateModal] = useState(false);
  const [migrationResult, setMigrationResult] = useState(null);

  const handleFixDates = async (dryRun) => {
    if (!migrationAccountId.trim()) {
      toast.error('Bitte Account-ID eingeben');
      return;
    }
    setMigrating(true);
    setMigrationResult(null);
    try {
      const response = await fetch(`${API}/admin/migrate/fix-trade-dates`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ account_id: migrationAccountId.trim(), dry_run: dryRun }),
      });
      const data = await response.json();
      if (response.ok) {
        setMigrationResult(data);
        if (!dryRun && data.fixed > 0) {
          toast.success(`${data.fixed} Trade-Daten korrigiert!`);
        }
      } else {
        toast.error(data.detail || 'Migration fehlgeschlagen');
      }
    } catch (error) {
      toast.error('Migration fehlgeschlagen');
    } finally {
      setMigrating(false);
    }
  };

  const handleStripeSync = async () => {
    setStripeSyncing(true);
    try {
      const response = await fetch(`${API}/admin/stripe/sync-subscriptions`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await response.json();
      if (response.ok) {
        if (data.synced > 0) {
          toast.success(`${data.synced} Abonnements synchronisiert`);
        } else {
          toast.info(`Alle ${data.total_checked} Abonnements sind bereits aktuell`);
        }
        if (data.synced > 0) {
          fetchUsers();
          fetchStats();
        }
      } else {
        toast.error(data.detail || 'Sync fehlgeschlagen');
      }
    } catch (error) {
      toast.error('Stripe-Sync fehlgeschlagen');
    } finally {
      setStripeSyncing(false);
    }
  };

  const handleDeactivateAllCoupons = async (dryRun = false) => {
    const confirmMsg = dryRun
      ? 'Dry-Run: Nur zeigen wie viele Codes deaktiviert würden. Keine Änderungen.'
      : 'ALLE Rabattcodes werden deaktiviert (Stripe + App-DB). Diese Aktion kann nicht rückgängig gemacht werden.\n\nWirklich fortfahren?';
    if (!window.confirm(confirmMsg)) return;
    setCleanupRunning(true);
    try {
      const url = `${API}/admin/coupons/deactivate-all${dryRun ? '?dry_run=true' : ''}`;
      const response = await fetch(url, { method: 'POST', credentials: 'include' });
      const data = await response.json();
      if (response.ok) {
        toast.success(
          `${data.dry_run ? '[Dry-Run] ' : ''}${data.stripe_promotion_codes_deactivated} Stripe-Codes deaktiviert, ` +
          `${data.stripe_coupons_deleted} Coupons gelöscht, ` +
          `${data.db_promos_cleared} DB-Einträge entfernt, ` +
          `${data.db_pricing_codes_deactivated} Pricing-Codes deaktiviert.`,
          { duration: 10000 }
        );
        if (!dryRun) {
          fetchPricing && fetchPricing();
        }
      } else {
        toast.error(data.detail || 'Deaktivierung fehlgeschlagen');
      }
    } catch (error) {
      toast.error('Deaktivierung fehlgeschlagen');
    } finally {
      setCleanupRunning(false);
    }
  };

  const handleCleanupInvoices = async (dryRun = false) => {
    const confirmMsg = dryRun
      ? 'Dry-Run starten? Es werden keine Mails verschickt oder Zahlungen ausgelöst — nur Bericht.'
      : 'Cleanup wirklich starten?\n\n1. Alle Kunden mit offenen Rechnungen bekommen eine Zahlungserinnerung.\n2. Stripe versucht die Zahlung erneut.\n3. Bei erneutem Fehlschlag: Abo gecancelt & Zugang gesperrt.';
    if (!window.confirm(confirmMsg)) return;
    setCleanupRunning(true);
    try {
      const url = `${API}/admin/payments/cleanup-open-invoices${dryRun ? '?dry_run=true' : ''}`;
      const response = await fetch(url, { method: 'POST', credentials: 'include' });
      const data = await response.json();
      if (response.ok) {
        toast.success(
          `${data.dry_run ? '[Dry-Run] ' : ''}${data.invoices_processed} Rechnungen bearbeitet — ` +
          `${data.emails_sent} Mails, ${data.retries_succeeded}/${data.retries_attempted} Retries erfolgreich, ` +
          `${data.subscriptions_cancelled} Abos gecancelt, ${data.users_blocked} Nutzer gesperrt.`,
          { duration: 10000 }
        );
        if (!dryRun) {
          fetchUsers();
          fetchStats();
        }
      } else {
        toast.error(data.detail || 'Cleanup fehlgeschlagen');
      }
    } catch (error) {
      toast.error('Cleanup fehlgeschlagen');
    } finally {
      setCleanupRunning(false);
    }
  };

  const handleKbAnnounce = async (variant) => {
    const label = variant === 'teaser' ? 'Ankündigungs-Teaser' : 'Launch-Mail';
    if (!window.confirm(`${label} jetzt an alle verifizierten Nutzer senden?\n\nBereits benachrichtigte Nutzer werden übersprungen.`)) return;
    try {
      const res = await fetch(`${API}/admin/knowledge-base/announce`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ variant })
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(`${label}: ${data.sent} gesendet, ${data.failed} fehlgeschlagen`);
      } else {
        toast.error(data.detail || 'Versand fehlgeschlagen');
      }
    } catch (e) {
      toast.error('Versand fehlgeschlagen');
    }
  };

  const [kbFilling, setKbFilling] = useState(false);
  const [kbGaps, setKbGaps] = useState(null);

  const loadKbGaps = async () => {
    try {
      const res = await fetch(`${API}/admin/knowledge-base/gaps`, { credentials: 'include' });
      if (res.ok) setKbGaps(await res.json());
    } catch (e) { /* noop */ }
  };

  const handleKbFillGaps = async () => {
    const input = window.prompt('Wie viele leere Themen füllen? (1–50)', '5');
    if (!input) return;
    const limit = Math.max(1, Math.min(50, parseInt(input) || 5));
    if (!window.confirm(`${limit} leere Themen per Perplexity Sonar Pro füllen?\n\nJeder Call kostet ~0,005–0,02 USD an Perplexity-Credits.`)) return;
    setKbFilling(true);
    try {
      const res = await fetch(`${API}/admin/knowledge-base/fill-gaps`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ limit, dry_run: false })
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(`✅ ${data.processed} gefüllt, ${data.failed} Fehler. Übrig: ${data.remaining_empty}`);
        loadKbGaps();
      } else {
        toast.error(data.detail || 'Fehler beim Füllen');
      }
    } catch (e) {
      toast.error('Fehler beim Füllen');
    } finally {
      setKbFilling(false);
    }
  };

  useEffect(() => { loadKbGaps(); }, []);

  const handleSaveSystemPrompt = async () => {
    setPromptLoading(true);
    setPromptSaved(false);
    try {
      const response = await fetch(`${API}/admin/system-prompt`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ prompt: systemPrompt }),
      });
      if (response.ok) {
        setPromptSaved(true);
        setTimeout(() => setPromptSaved(false), 3000);
      }
    } catch (error) {
      console.error('Error saving system prompt:', error);
    } finally {
      setPromptLoading(false);
    }
  };

  const handleSaveSkillScorePrompt = async () => {
    setSkillPromptLoading(true);
    setSkillPromptSaved(false);
    try {
      const response = await fetch(`${API}/admin/skill-score-prompt`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ prompt: skillScorePrompt }),
      });
      if (response.ok) {
        setSkillPromptSaved(true);
        setTimeout(() => setSkillPromptSaved(false), 3000);
      }
    } catch (error) {
      console.error('Error saving skill score prompt:', error);
    } finally {
      setSkillPromptLoading(false);
    }
  };

  const handleSavePricing = async () => {
    setPricingLoading(true);
    setPricingSaved(false);
    try {
      const response = await fetch(`${API}/admin/pricing`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(pricing),
      });
      if (response.ok) {
        setPricingSaved(true);
        setTimeout(() => setPricingSaved(false), 3000);
      }
    } catch (error) {
      console.error('Error saving pricing:', error);
    } finally {
      setPricingLoading(false);
    }
  };

  const updatePlanPrice = (plan, field, value) => {
    setPricing(prev => ({
      ...prev,
      [plan]: {
        ...prev[plan],
        [field]: value
      }
    }));
  };

  const addPromoCode = (plan, type = 'stripe') => {
    const field = type === 'lifetime' ? 'lifetime_promo_codes' : 'promo_codes';
    const newEntry = type === 'lifetime'
      ? { code: '', discount_percent: 0, expires: '', active: true }
      : { code: '', discount_percent: 0, expires: '', active: true, duration: 'forever', duration_in_months: 3 };
    setPricing(prev => ({
      ...prev,
      [plan]: {
        ...prev[plan],
        [field]: [...(prev[plan][field] || []), newEntry]
      }
    }));
  };

  const removePromoCode = (plan, index, type = 'stripe') => {
    const field = type === 'lifetime' ? 'lifetime_promo_codes' : 'promo_codes';
    setPricing(prev => ({
      ...prev,
      [plan]: {
        ...prev[plan],
        [field]: (prev[plan][field] || []).filter((_, i) => i !== index)
      }
    }));
  };

  const updatePromoCode = (plan, index, key, value, type = 'stripe') => {
    const field = type === 'lifetime' ? 'lifetime_promo_codes' : 'promo_codes';
    setPricing(prev => {
      const codes = [...(prev[plan][field] || [])];
      codes[index] = { ...codes[index], [key]: value };
      return { ...prev, [plan]: { ...prev[plan], [field]: codes } };
    });
  };

  const calculateDiscountedPrice = (plan) => {
    const planData = pricing[plan];
    if (!planData.discount_active || planData.discount_percent === 0) {
      return planData.price;
    }
    return (planData.price * (1 - planData.discount_percent / 100)).toFixed(2);
  };

  // Ticket handlers
  const fetchTicketDetails = async (ticketId) => {
    try {
      const response = await fetch(`${API}/admin/tickets/${ticketId}`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setSelectedTicket(data);
        setPendingStatus(null); // Reset pending status when loading new ticket
        setStatusNote('');
        setAdminComment('');
        setShowTicketModal(true);
      }
    } catch (error) {
      console.error('Error fetching ticket:', error);
    }
  };

  const handleUpdateTicketStatus = async (status) => {
    // Just set the pending status, don't save yet
    setPendingStatus(status);
  };

  const handleSaveTicketChanges = async () => {
    if (!selectedTicket) return;
    setSavingTicket(true);
    
    try {
      // First, add comment if there is one
      if (adminComment.trim()) {
        await fetch(`${API}/admin/tickets/${selectedTicket.ticket_id}/comments`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ text: adminComment })
        });
      }
      
      // Then update status if changed (this triggers email)
      const newStatus = pendingStatus || selectedTicket.status;
      if (pendingStatus && pendingStatus !== selectedTicket.status) {
        const response = await fetch(`${API}/admin/tickets/${selectedTicket.ticket_id}/status`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ status: newStatus, admin_note: statusNote || null })
        });
        
        if (response.ok) {
          toast.success('Ticket gespeichert', {
            description: `Status: ${newStatus === 'open' ? 'Offen' : newStatus === 'in_progress' ? 'In Bearbeitung' : 'Erledigt'}. E-Mail wurde gesendet.`
          });
        }
      } else if (adminComment.trim()) {
        toast.success('Kommentar gespeichert');
      }
      
      // Reset form and refresh
      setAdminComment('');
      setStatusNote('');
      setPendingStatus(null);
      fetchTickets();
      fetchTicketStats();
      fetchTicketDetails(selectedTicket.ticket_id);
      
    } catch (error) {
      console.error('Error saving ticket changes:', error);
      toast.error('Fehler beim Speichern');
    } finally {
      setSavingTicket(false);
    }
  };

  const handleAddAdminComment = async () => {
    // This is now handled by handleSaveTicketChanges
    // Keep for backwards compatibility but redirect to save
    if (!adminComment.trim()) return;
    await handleSaveTicketChanges();
  };

  const getPlanBadgeColor = (plan) => {
    const colors = {
      basic: 'bg-zinc-700 text-zinc-300',
      pro: 'bg-blue-600 text-white',
      premium: 'bg-gradient-to-r from-purple-600 to-pink-600 text-white'
    };
    return colors[plan] || colors.basic;
  };

  const getStatusBadge = (status) => {
    const config = {
      open: { label: language === 'de' ? 'Offen' : 'Open', className: 'bg-amber-500/20 text-amber-400', icon: <AlertCircle className="h-3 w-3" /> },
      in_progress: { label: language === 'de' ? 'In Bearbeitung' : 'In Progress', className: 'bg-blue-500/20 text-blue-400', icon: <Clock className="h-3 w-3" /> },
      done: { label: language === 'de' ? 'Erledigt' : 'Done', className: 'bg-emerald-500/20 text-emerald-400', icon: <CheckCircle2 className="h-3 w-3" /> }
    };
    const c = config[status] || config.open;
    return <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${c.className}`}>{c.icon}{c.label}</span>;
  };

  const getCategoryBadge = (category) => {
    if (category === 'bug') return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-rose-500/20 text-rose-400"><Bug className="h-3 w-3" />Bug</span>;
    return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-purple-500/20 text-purple-400"><Lightbulb className="h-3 w-3" />Feature</span>;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString(language === 'de' ? 'de-DE' : 'en-US', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  return (
    <TooltipProvider>
      <div className="min-h-screen bg-background flex">
        {/* Sidebar */}
        <Sidebar user={user} onLogout={handleLogout} />

        {/* Main Content */}
        <main className="flex-1 p-6">
          <div className="max-w-7xl mx-auto">
            {/* Header */}
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 rounded-lg bg-gradient-to-br from-purple-500/20 to-pink-500/20 border border-purple-500/30">
                  <Shield className="h-8 w-8 text-purple-400" />
                </div>
                <div>
                  <h1 className="text-3xl font-heading font-bold">Admin Dashboard</h1>
                  <p className="text-muted-foreground">{language === 'de' ? 'Plattform-Verwaltung & Einstellungen' : 'Platform Management & Settings'}</p>
                </div>
                <div className="ml-auto flex gap-2">
                  <Button
                    data-testid="kb-fill-gaps-btn"
                    variant="outline"
                    size="sm"
                    onClick={handleKbFillGaps}
                    disabled={kbFilling}
                    title={kbGaps ? `${kbGaps.empty}/${kbGaps.total} leer` : ''}
                    className="border-violet-500/30 text-violet-400 hover:bg-violet-500/10"
                  >
                    <Sparkles className={`h-4 w-4 mr-2 ${kbFilling ? 'animate-pulse' : ''}`} />
                    {kbFilling ? 'Fülle...' : `KB Lücken${kbGaps ? ` (${kbGaps.empty})` : ''}`}
                  </Button>
                  <Button
                    data-testid="kb-announce-teaser-btn"
                    variant="outline"
                    size="sm"
                    onClick={() => handleKbAnnounce('teaser')}
                    className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"
                  >
                    <GraduationCap className="h-4 w-4 mr-2" />
                    KB Teaser
                  </Button>
                  <Button
                    data-testid="kb-announce-launch-btn"
                    variant="outline"
                    size="sm"
                    onClick={() => handleKbAnnounce('launch')}
                    className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"
                  >
                    <Rocket className="h-4 w-4 mr-2" />
                    KB Launch
                  </Button>
                  <Button
                    data-testid="fix-dates-btn"
                    variant="outline"
                    size="sm"
                    onClick={() => setShowMigrateModal(true)}
                    className="border-orange-500/30 text-orange-400 hover:bg-orange-500/10"
                  >
                    <Clock className="h-4 w-4 mr-2" />
                    Daten reparieren
                  </Button>
                  <Button
                    data-testid="stripe-sync-btn"
                    variant="outline"
                    size="sm"
                    onClick={handleStripeSync}
                    disabled={stripeSyncing}
                    className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10"
                  >
                    <RefreshCw className={`h-4 w-4 mr-2 ${stripeSyncing ? 'animate-spin' : ''}`} />
                    {stripeSyncing ? 'Synchronisiere...' : 'Stripe Sync'}
                  </Button>
                  <Button
                    data-testid="cleanup-invoices-dry-btn"
                    variant="outline"
                    size="sm"
                    onClick={() => handleCleanupInvoices(true)}
                    disabled={cleanupRunning}
                    className="border-sky-500/30 text-sky-400 hover:bg-sky-500/10"
                    title="Offene Rechnungen analysieren (Dry-Run)"
                  >
                    <Search className={`h-4 w-4 mr-2 ${cleanupRunning ? 'animate-spin' : ''}`} />
                    Cleanup Preview
                  </Button>
                  <Button
                    data-testid="cleanup-invoices-btn"
                    variant="outline"
                    size="sm"
                    onClick={() => handleCleanupInvoices(false)}
                    disabled={cleanupRunning}
                    className="border-rose-500/30 text-rose-400 hover:bg-rose-500/10"
                    title="Mahnung senden + Retry + ggf. Sperre"
                  >
                    <AlertTriangle className={`h-4 w-4 mr-2 ${cleanupRunning ? 'animate-spin' : ''}`} />
                    {cleanupRunning ? 'Läuft...' : 'Offene Zahlungen bereinigen'}
                  </Button>
                  <Button
                    data-testid="deactivate-all-coupons-btn"
                    variant="outline"
                    size="sm"
                    onClick={() => handleDeactivateAllCoupons(false)}
                    disabled={cleanupRunning}
                    className="border-red-600/40 text-red-500 hover:bg-red-600/10"
                    title="ALLE Stripe- und App-Rabattcodes deaktivieren"
                  >
                    <AlertTriangle className={`h-4 w-4 mr-2`} />
                    Alle Rabattcodes deaktivieren
                  </Button>
                </div>
              </div>
            </div>

            {/* Stats Cards */}
            {stats && (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <Card className="bg-zinc-900/50 border-zinc-800"><CardContent className="pt-6"><div className="flex items-center justify-between"><div><p className="text-sm text-muted-foreground">{language === 'de' ? 'Benutzer' : 'Users'}</p><p className="text-2xl font-bold text-white">{stats.total_users}</p></div><Users className="h-8 w-8 text-emerald-400" /></div></CardContent></Card>
                <Card className="bg-zinc-900/50 border-zinc-800"><CardContent className="pt-6"><div className="flex items-center justify-between"><div><p className="text-sm text-muted-foreground">Accounts</p><p className="text-2xl font-bold text-white">{stats.total_accounts}</p></div><Briefcase className="h-8 w-8 text-blue-400" /></div></CardContent></Card>
                <Card className="bg-zinc-900/50 border-zinc-800"><CardContent className="pt-6"><div className="flex items-center justify-between"><div><p className="text-sm text-muted-foreground">Trades</p><p className="text-2xl font-bold text-white">{stats.total_trades}</p></div><BarChart3 className="h-8 w-8 text-emerald-400" /></div></CardContent></Card>
                <Card className="bg-zinc-900/50 border-zinc-800"><CardContent className="pt-6"><div className="flex items-center justify-between"><div><p className="text-sm text-muted-foreground">MRR</p><p className="text-2xl font-bold text-emerald-400">€{stats.monthly_revenue}</p>{stats.lifetime_revenue > 0 && <p className="text-xs text-amber-400 mt-1">+ €{stats.lifetime_revenue} Lifetime</p>}</div><DollarSign className="h-8 w-8 text-emerald-400" /></div></CardContent></Card>
              </div>
            )}

            {/* Tabs */}
            <Tabs defaultValue="users" className="w-full">
              <TabsList className="mb-6">
                <TabsTrigger value="users"><Users className="h-4 w-4 mr-2" />{language === 'de' ? 'Benutzer' : 'Users'}</TabsTrigger>
                <TabsTrigger value="tickets"><Ticket className="h-4 w-4 mr-2" />Ticketcenter</TabsTrigger>
                <TabsTrigger value="system-prompt"><Brain className="h-4 w-4 mr-2" />{language === 'de' ? 'KI Mentor' : 'AI Mentor'}</TabsTrigger>
                <TabsTrigger value="pricing"><DollarSign className="h-4 w-4 mr-2" />{language === 'de' ? 'Preise' : 'Pricing'}</TabsTrigger>
              </TabsList>

              {/* Users Tab */}
              <TabsContent value="users">
                <Card className="bg-zinc-900/50 border-zinc-800 mb-6">
                  <CardContent className="pt-6">
                    <div className="flex flex-wrap gap-4">
                      <div className="flex-1 min-w-[250px]">
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                          <Input type="text" placeholder={language === 'de' ? 'Suche nach E-Mail oder Name...' : 'Search by email or name...'} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" />
                        </div>
                      </div>
                      <div className="flex gap-2">
                        {['all', 'basic', 'pro', 'premium'].map(p => (
                          <Button key={p} variant={planFilter === p ? 'default' : 'outline'} size="sm" onClick={() => setPlanFilter(p)}>
                            {p === 'all' ? (language === 'de' ? 'Alle' : 'All') : p.charAt(0).toUpperCase() + p.slice(1)}
                          </Button>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-zinc-900/50 border-zinc-800">
                  <CardHeader><CardTitle>{language === 'de' ? 'Benutzer' : 'Users'} ({filteredUsers.length})</CardTitle></CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-zinc-800">
                            <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">E-Mail</th>
                            <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Name</th>
                            <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Plan</th>
                            <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Accounts</th>
                            <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{language === 'de' ? 'Einnahmen' : 'Revenue'}</th>
                            <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{language === 'de' ? 'Seit' : 'Since'}</th>
                            <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">{language === 'de' ? 'Aktionen' : 'Actions'}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {loading ? (<tr><td colSpan="7" className="text-center py-8 text-muted-foreground">{language === 'de' ? 'Lädt...' : 'Loading...'}</td></tr>)
                          : filteredUsers.length === 0 ? (<tr><td colSpan="7" className="text-center py-8 text-muted-foreground">{language === 'de' ? 'Keine Benutzer gefunden' : 'No users found'}</td></tr>)
                          : filteredUsers.map((usr) => (
                            <tr key={usr.user_id} className="border-b border-zinc-800 hover:bg-zinc-800/50 transition-colors">
                              <td className="py-3 px-4 text-sm">{usr.email}</td>
                              <td className="py-3 px-4 text-sm">{usr.name}</td>
                              <td className="py-3 px-4"><span className={`text-xs px-2 py-1 rounded-full ${getPlanBadgeColor(usr.subscription_plan)}`}>{usr.subscription_plan.charAt(0).toUpperCase() + usr.subscription_plan.slice(1)}</span></td>
                              <td className="py-3 px-4 text-sm">{usr.account_count}</td>
                              <td className="py-3 px-4 text-sm">
                                <span className={usr.monthly_revenue > 0 ? (usr.is_lifetime ? 'text-amber-400' : 'text-emerald-400') : 'text-muted-foreground'}>€{Number(usr.monthly_revenue).toFixed(2)}</span>
                                {usr.revenue_note && usr.revenue_note !== 'Inaktiv' && usr.monthly_revenue > 0 && (
                                  <span className="text-[10px] text-muted-foreground ml-1">
                                    {usr.is_lifetime ? '(einmalig)' : '/Mo'}
                                  </span>
                                )}
                              </td>
                              <td className="py-3 px-4 text-sm text-muted-foreground">{formatDate(usr.subscription_start)?.split(',')[0]}</td>
                              <td className="py-3 px-4">
                                <div className="flex items-center justify-end gap-2">
                                  <Tooltip><TooltipTrigger asChild><Button variant="ghost" size="sm" onClick={() => { setSelectedUser(usr); setNewPlan(usr.subscription_plan); setShowEditModal(true); }}><Edit className="h-4 w-4" /></Button></TooltipTrigger><TooltipContent>{language === 'de' ? 'Abo ändern' : 'Change Plan'}</TooltipContent></Tooltip>
                                  <Tooltip><TooltipTrigger asChild><Button variant="ghost" size="sm" onClick={() => { setSelectedUser(usr); setShowResetModal(true); }}><Mail className="h-4 w-4" /></Button></TooltipTrigger><TooltipContent>{language === 'de' ? 'Passwort zurücksetzen' : 'Reset Password'}</TooltipContent></Tooltip>
                                  <Tooltip><TooltipTrigger asChild><Button variant="ghost" size="sm" onClick={() => { setSelectedUser(usr); setShowDeleteModal(true); }} className="text-rose-400 hover:text-rose-300"><Trash2 className="h-4 w-4" /></Button></TooltipTrigger><TooltipContent>{language === 'de' ? 'Löschen' : 'Delete'}</TooltipContent></Tooltip>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Tickets Tab */}
              <TabsContent value="tickets">
                {/* Ticket Stats */}
                {ticketStats && (
                  <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
                    <Card className="bg-zinc-900/50 border-zinc-800"><CardContent className="pt-4 pb-4 text-center"><p className="text-xs text-muted-foreground">Gesamt</p><p className="text-2xl font-bold">{ticketStats.total}</p></CardContent></Card>
                    <Card className="bg-amber-500/10 border-amber-500/30"><CardContent className="pt-4 pb-4 text-center"><p className="text-xs text-amber-400">Offen</p><p className="text-2xl font-bold text-amber-400">{ticketStats.open}</p></CardContent></Card>
                    <Card className="bg-blue-500/10 border-blue-500/30"><CardContent className="pt-4 pb-4 text-center"><p className="text-xs text-blue-400">In Bearbeitung</p><p className="text-2xl font-bold text-blue-400">{ticketStats.in_progress}</p></CardContent></Card>
                    <Card className="bg-emerald-500/10 border-emerald-500/30"><CardContent className="pt-4 pb-4 text-center"><p className="text-xs text-emerald-400">Erledigt</p><p className="text-2xl font-bold text-emerald-400">{ticketStats.done}</p></CardContent></Card>
                    <Card className="bg-rose-500/10 border-rose-500/30"><CardContent className="pt-4 pb-4 text-center"><p className="text-xs text-rose-400">Bugs</p><p className="text-2xl font-bold text-rose-400">{ticketStats.bugs}</p></CardContent></Card>
                    <Card className="bg-purple-500/10 border-purple-500/30"><CardContent className="pt-4 pb-4 text-center"><p className="text-xs text-purple-400">Features</p><p className="text-2xl font-bold text-purple-400">{ticketStats.features}</p></CardContent></Card>
                  </div>
                )}

                {/* Filters */}
                <Card className="bg-zinc-900/50 border-zinc-800 mb-6">
                  <CardContent className="pt-6">
                    <div className="flex flex-wrap gap-4">
                      <div>
                        <Label className="text-xs mb-1 block">Status</Label>
                        <div className="flex gap-1">
                          {['all', 'open', 'in_progress', 'done'].map(s => (
                            <Button key={s} variant={ticketFilter.status === s ? 'default' : 'outline'} size="sm" onClick={() => setTicketFilter(prev => ({ ...prev, status: s }))}>
                              {s === 'all' ? 'Alle' : s === 'open' ? 'Offen' : s === 'in_progress' ? 'In Bearbeitung' : 'Erledigt'}
                            </Button>
                          ))}
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs mb-1 block">Kategorie</Label>
                        <div className="flex gap-1">
                          {['all', 'bug', 'feature'].map(c => (
                            <Button key={c} variant={ticketFilter.category === c ? 'default' : 'outline'} size="sm" onClick={() => setTicketFilter(prev => ({ ...prev, category: c }))}>
                              {c === 'all' ? 'Alle' : c === 'bug' ? 'Bug' : 'Feature'}
                            </Button>
                          ))}
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs mb-1 block">Abo-Plan</Label>
                        <div className="flex gap-1">
                          {['all', 'premium', 'pro', 'basic'].map(p => (
                            <Button key={p} variant={ticketFilter.plan === p ? 'default' : 'outline'} size="sm" onClick={() => setTicketFilter(prev => ({ ...prev, plan: p }))}>
                              {p === 'all' ? 'Alle' : p.charAt(0).toUpperCase() + p.slice(1)}
                            </Button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Tickets Table */}
                <Card className="bg-zinc-900/50 border-zinc-800">
                  <CardHeader><CardTitle>Tickets ({tickets.length})</CardTitle></CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-zinc-800">
                            <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Ticket</th>
                            <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Kunde</th>
                            <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Plan</th>
                            <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Kategorie</th>
                            <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Status</th>
                            <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Datum</th>
                            <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Aktionen</th>
                          </tr>
                        </thead>
                        <tbody>
                          {tickets.length === 0 ? (
                            <tr><td colSpan="7" className="text-center py-8 text-muted-foreground">Keine Tickets gefunden</td></tr>
                          ) : tickets.map((ticket) => (
                            <tr key={ticket.ticket_id} className="border-b border-zinc-800 hover:bg-zinc-800/50 transition-colors">
                              <td className="py-3 px-4">
                                <div>
                                  <span className="text-xs font-mono text-emerald-400">{ticket.ticket_number}</span>
                                  <p className="text-sm font-medium truncate max-w-[200px]">{ticket.title}</p>
                                </div>
                              </td>
                              <td className="py-3 px-4 text-sm">{ticket.user_email}</td>
                              <td className="py-3 px-4"><span className={`text-xs px-2 py-1 rounded-full ${getPlanBadgeColor(ticket.subscription_plan)}`}>{ticket.subscription_plan?.charAt(0).toUpperCase() + ticket.subscription_plan?.slice(1)}</span></td>
                              <td className="py-3 px-4">{getCategoryBadge(ticket.category)}</td>
                              <td className="py-3 px-4">{getStatusBadge(ticket.status)}</td>
                              <td className="py-3 px-4 text-sm text-muted-foreground">{formatDate(ticket.created_at)?.split(',')[0]}</td>
                              <td className="py-3 px-4">
                                <div className="flex items-center justify-end gap-2">
                                  <Tooltip><TooltipTrigger asChild><Button variant="ghost" size="sm" onClick={() => fetchTicketDetails(ticket.ticket_id)}><Eye className="h-4 w-4" /></Button></TooltipTrigger><TooltipContent>Details</TooltipContent></Tooltip>
                                  {ticket.screenshot_url && (<Tooltip><TooltipTrigger asChild><span className="text-muted-foreground"><Image className="h-4 w-4" /></span></TooltipTrigger><TooltipContent>Hat Screenshot</TooltipContent></Tooltip>)}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* System Prompt Tab */}
              <TabsContent value="system-prompt">
                <Card className="bg-zinc-900/50 border-zinc-800">
                  <CardHeader>
                    <CardTitle>{language === 'de' ? 'KI Mentor System Prompt' : 'AI Mentor System Prompt'}</CardTitle>
                    <CardDescription>{language === 'de' ? 'Bearbeiten Sie den System-Prompt für den KI Trading Mentor.' : 'Edit the system prompt for the AI Trading Mentor.'}</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label className="mb-2 block">System Prompt</Label>
                      <Textarea value={systemPrompt} onChange={(e) => setSystemPrompt(e.target.value)} className="min-h-[400px] font-mono text-sm" disabled={promptLoading} />
                      <p className="text-xs text-muted-foreground mt-2">{systemPrompt.length} {language === 'de' ? 'Zeichen' : 'characters'}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <Button onClick={handleSaveSystemPrompt} disabled={promptLoading || systemPrompt.length < 50} className="glow-primary">
                        {promptLoading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{language === 'de' ? 'Speichert...' : 'Saving...'}</> : <><Save className="h-4 w-4 mr-2" />{language === 'de' ? 'Speichern' : 'Save'}</>}
                      </Button>
                      {promptSaved && <div className="flex items-center gap-2 text-emerald-400"><CheckCircle2 className="h-4 w-4" /><span className="text-sm">{language === 'de' ? 'Erfolgreich gespeichert!' : 'Successfully saved!'}</span></div>}
                    </div>
                  </CardContent>
                </Card>
                
                {/* Skill Score Prompt */}
                <Card className="bg-zinc-900/50 border-zinc-800 mt-6">
                  <CardHeader>
                    <CardTitle>{language === 'de' ? 'Skill-Score System Prompt' : 'Skill Score System Prompt'}</CardTitle>
                    <CardDescription>{language === 'de' ? 'Dieser Prompt steuert, wie die 4 Skill-Scores berechnet werden.' : 'This prompt controls how the 4 skill scores are calculated.'}</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label className="mb-2 block">Skill Score Prompt</Label>
                      <Textarea value={skillScorePrompt} onChange={(e) => setSkillScorePrompt(e.target.value)} className="min-h-[400px] font-mono text-sm" disabled={skillPromptLoading} />
                      <p className="text-xs text-muted-foreground mt-2">{skillScorePrompt.length} {language === 'de' ? 'Zeichen' : 'characters'}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <Button onClick={handleSaveSkillScorePrompt} disabled={skillPromptLoading || skillScorePrompt.length < 50} className="glow-primary">
                        {skillPromptLoading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{language === 'de' ? 'Speichert...' : 'Saving...'}</> : <><Save className="h-4 w-4 mr-2" />{language === 'de' ? 'Speichern' : 'Save'}</>}
                      </Button>
                      {skillPromptSaved && <div className="flex items-center gap-2 text-emerald-400"><CheckCircle2 className="h-4 w-4" /><span className="text-sm">{language === 'de' ? 'Erfolgreich gespeichert!' : 'Successfully saved!'}</span></div>}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Pricing Tab */}
              <TabsContent value="pricing">
                <Card className="bg-zinc-900/50 border-zinc-800">
                  <CardHeader>
                    <CardTitle>{language === 'de' ? 'Abo-Preise & Rabatte' : 'Subscription Pricing & Discounts'}</CardTitle>
                    <CardDescription>{language === 'de' ? 'Verwalten Sie Preise, Rabatte und Gutschein-Codes.' : 'Manage prices, discounts and promotional codes.'}</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {['basic', 'pro', 'premium'].map((plan) => (
                      <Card key={plan} className="bg-zinc-800/50 border-zinc-700">
                        <CardContent className="pt-6">
                          <div className="flex items-start justify-between mb-4">
                            <div><h3 className="text-lg font-semibold capitalize">{plan}</h3><p className="text-sm text-muted-foreground">{plan === 'basic' && '1 Account'}{plan === 'pro' && '5 Accounts'}{plan === 'premium' && 'Unbegrenzte Accounts'}</p></div>
                            <div className="flex items-center gap-2"><Switch checked={pricing[plan].discount_active} onCheckedChange={(checked) => updatePlanPrice(plan, 'discount_active', checked)} /><Label>{language === 'de' ? 'Rabatt aktiv' : 'Discount active'}</Label></div>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                            <div><Label>{language === 'de' ? 'Originalpreis (€)' : 'Original Price (€)'}</Label><Input type="number" step="0.01" value={pricing[plan].price} onChange={(e) => updatePlanPrice(plan, 'price', parseFloat(e.target.value))} className="mt-1" /></div>
                            <div><Label>{language === 'de' ? 'Rabatt (%)' : 'Discount (%)'}</Label><Input type="number" min="0" max="100" value={pricing[plan].discount_percent} onChange={(e) => updatePlanPrice(plan, 'discount_percent', parseInt(e.target.value) || 0)} disabled={!pricing[plan].discount_active} className="mt-1" /></div>
                            <div><Label>{language === 'de' ? 'Neuer Preis' : 'New Price'}</Label><div className="mt-1 p-2 rounded-lg bg-zinc-900 border border-zinc-700">{pricing[plan].discount_active && pricing[plan].discount_percent > 0 ? (<div className="flex items-center gap-2"><span className="text-sm line-through text-muted-foreground">€{pricing[plan].price.toFixed(2)}</span><span className="text-lg font-bold text-emerald-400">€{calculateDiscountedPrice(plan)}</span></div>) : (<span className="text-lg font-bold">€{pricing[plan].price.toFixed(2)}</span>)}</div></div>
                          </div>
                          
                          {/* Yearly Discount Section */}
                          <div className="border-t border-zinc-700 pt-4 mb-4">
                            <div className="flex items-center gap-2 mb-3">
                              <DollarSign className="h-4 w-4 text-blue-400" />
                              <span className="text-sm font-medium text-blue-400">{language === 'de' ? 'Jahresabo-Rabatt' : 'Yearly Subscription Discount'}</span>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              <div>
                                <Label className="text-xs">{language === 'de' ? 'Jahresrabatt (%)' : 'Yearly Discount (%)'}</Label>
                                <Input 
                                  type="number" 
                                  min="0" 
                                  max="100" 
                                  value={pricing[plan].yearly_discount_percent || 0} 
                                  onChange={(e) => updatePlanPrice(plan, 'yearly_discount_percent', parseInt(e.target.value) || 0)} 
                                  className="mt-1"
                                  data-testid={`yearly-discount-input-${plan}`}
                                />
                              </div>
                              <div>
                                <Label className="text-xs">{language === 'de' ? 'Monatspreis' : 'Monthly Price'}</Label>
                                <div className="mt-1 p-2 rounded-lg bg-zinc-900 border border-zinc-700">
                                  <span className="text-sm">€{parseFloat(calculateDiscountedPrice(plan)).toFixed(2)}/Mo</span>
                                </div>
                              </div>
                              <div>
                                <Label className="text-xs">{language === 'de' ? 'Jahrespreis (Vorschau)' : 'Yearly Price (Preview)'}</Label>
                                <div className="mt-1 p-2 rounded-lg bg-zinc-900 border border-zinc-700">
                                  {(() => {
                                    const monthly = parseFloat(calculateDiscountedPrice(plan));
                                    const yd = pricing[plan].yearly_discount_percent || 0;
                                    const yearlyFull = monthly * 12;
                                    const yearlyDiscounted = (yearlyFull * (1 - yd / 100)).toFixed(2);
                                    const saving = (yearlyFull - yearlyDiscounted).toFixed(2);
                                    return yd > 0 ? (
                                      <div>
                                        <div className="flex items-center gap-2">
                                          <span className="text-sm line-through text-muted-foreground">€{yearlyFull.toFixed(2)}</span>
                                          <span className="text-lg font-bold text-blue-400">€{yearlyDiscounted}</span>
                                          <span className="text-xs text-muted-foreground">/Jahr</span>
                                        </div>
                                        <p className="text-xs text-emerald-400 mt-1">{language === 'de' ? 'Ersparnis' : 'Savings'}: €{saving}</p>
                                      </div>
                                    ) : (
                                      <span className="text-lg font-bold">€{yearlyFull.toFixed(2)}/Jahr</span>
                                    );
                                  })()}
                                </div>
                              </div>
                            </div>
                          </div>
                          
                          {/* Lifetime Price Section - Only for Premium */}
                          {plan === 'premium' && (
                            <div className="border-t border-zinc-700 pt-4 mb-4">
                              <div className="flex items-center gap-2 mb-3">
                                <DollarSign className="h-4 w-4 text-amber-400" />
                                <span className="text-sm font-medium text-amber-400">{language === 'de' ? 'Lifetime-Preis (Einmalzahlung)' : 'Lifetime Price (One-time)'}</span>
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                  <Label className="text-xs">{language === 'de' ? 'Lifetime-Preis (€)' : 'Lifetime Price (€)'}</Label>
                                  <Input 
                                    type="number" 
                                    step="0.01"
                                    min="0" 
                                    value={pricing[plan].lifetime_price || 499.99} 
                                    onChange={(e) => updatePlanPrice(plan, 'lifetime_price', parseFloat(e.target.value) || 0)} 
                                    className="mt-1"
                                    data-testid="lifetime-price-input"
                                  />
                                </div>
                                <div>
                                  <Label className="text-xs">{language === 'de' ? 'Vorschau' : 'Preview'}</Label>
                                  <div className="mt-1 p-2 rounded-lg bg-zinc-900 border border-amber-500/30">
                                    <span className="text-lg font-bold text-amber-400">€{(pricing[plan].lifetime_price || 499.99).toFixed(2)}</span>
                                    <span className="text-xs text-muted-foreground ml-1">{language === 'de' ? 'einmalig' : 'one-time'}</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Lifetime Promo Codes Section - Only for Premium */}
                          {plan === 'premium' && (
                            <div className="border-t border-zinc-700 pt-4 mb-4">
                              <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-2">
                                  <Tag className="h-4 w-4 text-amber-400" />
                                  <span className="text-sm font-medium text-amber-400">{language === 'de' ? 'Lifetime Rabatt-Codes (PayPal)' : 'Lifetime Promo Codes (PayPal)'}</span>
                                </div>
                                <Button variant="outline" size="sm" onClick={() => addPromoCode(plan, 'lifetime')} className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10 text-xs h-7" data-testid="add-lifetime-promo-btn">
                                  <Plus className="h-3 w-3 mr-1" />{language === 'de' ? 'Code hinzufügen' : 'Add Code'}
                                </Button>
                              </div>
                              {(pricing[plan].lifetime_promo_codes || []).length === 0 && (
                                <p className="text-xs text-muted-foreground">{language === 'de' ? 'Noch keine Lifetime-Codes angelegt.' : 'No lifetime codes created yet.'}</p>
                              )}
                              <div className="space-y-3">
                                {(pricing[plan].lifetime_promo_codes || []).map((promo, idx) => (
                                  <div key={idx} className={`p-3 rounded-lg border ${promo.active ? 'border-amber-500/30 bg-amber-500/5' : 'border-zinc-700 bg-zinc-800/50 opacity-60'}`} data-testid={`lifetime-promo-row-${idx}`}>
                                    <div className="flex items-center justify-between mb-2">
                                      <div className="flex items-center gap-2">
                                        <Switch checked={promo.active} onCheckedChange={(v) => updatePromoCode(plan, idx, 'active', v, 'lifetime')} data-testid={`lifetime-promo-active-${idx}`} />
                                        <span className="text-xs text-muted-foreground">{promo.active ? (language === 'de' ? 'Aktiv' : 'Active') : (language === 'de' ? 'Inaktiv' : 'Inactive')}</span>
                                      </div>
                                      <button onClick={() => removePromoCode(plan, idx, 'lifetime')} className="text-zinc-500 hover:text-red-400 transition-colors" data-testid={`lifetime-promo-delete-${idx}`}><Trash2 className="h-3.5 w-3.5" /></button>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                                      <div>
                                        <Label className="text-xs">{language === 'de' ? 'Code' : 'Code'}</Label>
                                        <Input type="text" placeholder="LIFETIME50" value={promo.code} onChange={(e) => updatePromoCode(plan, idx, 'code', e.target.value.toUpperCase(), 'lifetime')} className="mt-1 uppercase h-8 text-sm" data-testid={`lifetime-promo-code-${idx}`} />
                                      </div>
                                      <div>
                                        <Label className="text-xs">{language === 'de' ? 'Rabatt (%)' : 'Discount (%)'}</Label>
                                        <Input type="number" min="1" max="100" placeholder="50" value={promo.discount_percent || ''} onChange={(e) => updatePromoCode(plan, idx, 'discount_percent', parseInt(e.target.value) || 0, 'lifetime')} className="mt-1 h-8 text-sm" data-testid={`lifetime-promo-discount-${idx}`} />
                                      </div>
                                      <div>
                                        <Label className="text-xs">{language === 'de' ? 'Gültig bis' : 'Valid until'}</Label>
                                        <Input type="date" value={promo.expires ? promo.expires.split('T')[0] : ''} onChange={(e) => updatePromoCode(plan, idx, 'expires', e.target.value ? new Date(e.target.value).toISOString() : '', 'lifetime')} className="mt-1 h-8 text-sm" data-testid={`lifetime-promo-expires-${idx}`} />
                                      </div>
                                      <div>
                                        <Label className="text-xs">{language === 'de' ? 'Vorschau' : 'Preview'}</Label>
                                        <div className="mt-1 p-1.5 rounded bg-zinc-900 border border-amber-500/20 text-xs min-h-[32px] flex items-center">
                                          {promo.active && promo.code && promo.discount_percent > 0 ? (
                                            <span><span className="font-mono text-amber-400">{promo.code}</span> <span className="text-amber-400">-{promo.discount_percent}%</span> <span className="text-muted-foreground">= €{((pricing[plan].lifetime_price || 499.99) * (1 - promo.discount_percent / 100)).toFixed(2)}</span></span>
                                          ) : <span className="text-muted-foreground">—</span>}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                              <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                                <HelpCircle className="h-3 w-3" />
                                {language === 'de' ? 'Kunden können diese Codes auf der Abo-Seite beim Lifetime PayPal-Checkout eingeben.' : 'Customers can enter these codes on the subscription page for Lifetime PayPal checkout.'}
                              </p>
                            </div>
                          )}
                          
                          {/* Stripe Promo Codes Section */}
                          <div className="border-t border-zinc-700 pt-4">
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center gap-2">
                                <DollarSign className="h-4 w-4 text-emerald-400" />
                                <span className="text-sm font-medium text-emerald-400">{language === 'de' ? 'Gutschein-Codes für Stripe Checkout' : 'Promo Codes for Stripe Checkout'}</span>
                              </div>
                              <Button variant="outline" size="sm" onClick={() => addPromoCode(plan, 'stripe')} className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10 text-xs h-7" data-testid={`add-stripe-promo-btn-${plan}`}>
                                <Plus className="h-3 w-3 mr-1" />{language === 'de' ? 'Code hinzufügen' : 'Add Code'}
                              </Button>
                            </div>
                            {(pricing[plan].promo_codes || []).length === 0 && (
                              <p className="text-xs text-muted-foreground">{language === 'de' ? 'Noch keine Stripe-Codes angelegt.' : 'No Stripe codes created yet.'}</p>
                            )}
                            <div className="space-y-3">
                              {(pricing[plan].promo_codes || []).map((promo, idx) => (
                                <div key={idx} className={`p-3 rounded-lg border ${promo.active ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-zinc-700 bg-zinc-800/50 opacity-60'}`} data-testid={`stripe-promo-row-${plan}-${idx}`}>
                                  <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-2">
                                      <Switch checked={promo.active} onCheckedChange={(v) => updatePromoCode(plan, idx, 'active', v, 'stripe')} data-testid={`stripe-promo-active-${plan}-${idx}`} />
                                      <span className="text-xs text-muted-foreground">{promo.active ? (language === 'de' ? 'Aktiv' : 'Active') : (language === 'de' ? 'Inaktiv' : 'Inactive')}</span>
                                    </div>
                                    <button onClick={() => removePromoCode(plan, idx, 'stripe')} className="text-zinc-500 hover:text-red-400 transition-colors" data-testid={`stripe-promo-delete-${plan}-${idx}`}><Trash2 className="h-3.5 w-3.5" /></button>
                                  </div>
                                  <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                                    <div>
                                      <Label className="text-xs">{language === 'de' ? 'Code' : 'Code'}</Label>
                                      <Input type="text" placeholder="SUMMER20" value={promo.code} onChange={(e) => updatePromoCode(plan, idx, 'code', e.target.value.toUpperCase(), 'stripe')} className="mt-1 uppercase h-8 text-sm" data-testid={`stripe-promo-code-${plan}-${idx}`} />
                                    </div>
                                    <div>
                                      <Label className="text-xs">{language === 'de' ? 'Rabatt (%)' : 'Discount (%)'}</Label>
                                      <Input type="number" min="1" max="100" placeholder="20" value={promo.discount_percent || ''} onChange={(e) => updatePromoCode(plan, idx, 'discount_percent', parseInt(e.target.value) || 0, 'stripe')} className="mt-1 h-8 text-sm" data-testid={`stripe-promo-discount-${plan}-${idx}`} />
                                    </div>
                                    <div>
                                      <Label className="text-xs">{language === 'de' ? 'Gültig bis' : 'Valid until'}</Label>
                                      <Input type="date" value={promo.expires ? promo.expires.split('T')[0] : ''} onChange={(e) => updatePromoCode(plan, idx, 'expires', e.target.value ? new Date(e.target.value).toISOString() : '', 'stripe')} className="mt-1 h-8 text-sm" data-testid={`stripe-promo-expires-${plan}-${idx}`} />
                                    </div>
                                    <div>
                                      <Label className="text-xs">{language === 'de' ? 'Vorschau' : 'Preview'}</Label>
                                      <div className="mt-1 p-1.5 rounded bg-zinc-900 border border-zinc-700 text-xs min-h-[32px] flex items-center">
                                        {promo.active && promo.code && promo.discount_percent > 0 ? (
                                          <span><span className="font-mono text-emerald-400">{promo.code}</span> <span className="text-emerald-400">-{promo.discount_percent}%</span>{
                                            (promo.duration || 'forever') === 'once' ? <span className="text-zinc-400 ml-1">{language === 'de' ? '· nur 1. Zahlung' : '· first payment only'}</span> :
                                            (promo.duration === 'repeating') ? <span className="text-zinc-400 ml-1">{language === 'de' ? `· ${promo.duration_in_months || 0} Zahlungen` : `· ${promo.duration_in_months || 0} payments`}</span> :
                                            <span className="text-zinc-400 ml-1">{language === 'de' ? '· dauerhaft' : '· forever'}</span>
                                          }</span>
                                        ) : <span className="text-muted-foreground">—</span>}
                                      </div>
                                    </div>
                                  </div>
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3 pt-3 border-t border-zinc-800/60">
                                    <div>
                                      <Label className="text-xs">{language === 'de' ? 'Rabatt-Dauer' : 'Discount duration'}</Label>
                                      <select
                                        value={promo.duration || 'forever'}
                                        onChange={(e) => updatePromoCode(plan, idx, 'duration', e.target.value, 'stripe')}
                                        className="mt-1 h-8 text-sm w-full rounded-md border border-zinc-700 bg-background px-2"
                                        data-testid={`stripe-promo-duration-${plan}-${idx}`}
                                      >
                                        <option value="once">{language === 'de' ? 'Einmalig (nur 1. Zahlung)' : 'Once (first payment only)'}</option>
                                        <option value="repeating">{language === 'de' ? 'Mehrere Zahlungen' : 'Multiple payments'}</option>
                                        <option value="forever">{language === 'de' ? 'Dauerhaft (alle Zahlungen)' : 'Forever (all payments)'}</option>
                                      </select>
                                    </div>
                                    <div>
                                      <Label className="text-xs">{language === 'de' ? 'Anzahl Monate / Zahlungen' : 'Number of months / payments'}</Label>
                                      <Input
                                        type="number"
                                        min="1"
                                        max="36"
                                        placeholder="3"
                                        disabled={(promo.duration || 'forever') !== 'repeating'}
                                        value={promo.duration === 'repeating' ? (promo.duration_in_months || '') : ''}
                                        onChange={(e) => updatePromoCode(plan, idx, 'duration_in_months', parseInt(e.target.value) || 0, 'stripe')}
                                        className="mt-1 h-8 text-sm disabled:opacity-50"
                                        data-testid={`stripe-promo-duration-months-${plan}-${idx}`}
                                      />
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                            <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                              <HelpCircle className="h-3 w-3" />
                              {language === 'de' ? 'Kunden können diese Codes beim Stripe Checkout eingeben.' : 'Customers can enter these codes at Stripe checkout.'}
                            </p>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                    <div className="flex items-center gap-3">
                      <Button onClick={handleSavePricing} disabled={pricingLoading} className="glow-primary" data-testid="save-pricing-btn">
                        {pricingLoading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{language === 'de' ? 'Speichert...' : 'Saving...'}</> : <><Save className="h-4 w-4 mr-2" />{language === 'de' ? 'Speichern' : 'Save'}</>}
                      </Button>
                      {pricingSaved && <div className="flex items-center gap-2 text-emerald-400"><CheckCircle2 className="h-4 w-4" /><span className="text-sm">{language === 'de' ? 'Erfolgreich gespeichert!' : 'Successfully saved!'}</span></div>}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </main>

        {/* Delete User Modal */}
        <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
          <DialogContent className="glass max-w-md">
            <DialogHeader><DialogTitle className="text-rose-400">{language === 'de' ? 'Benutzer löschen' : 'Delete User'}</DialogTitle><DialogDescription>{language === 'de' ? `Möchten Sie ${selectedUser?.email} wirklich löschen?` : `Are you sure you want to delete ${selectedUser?.email}?`}</DialogDescription></DialogHeader>
            <DialogFooter><Button variant="outline" onClick={() => setShowDeleteModal(false)}>{language === 'de' ? 'Abbrechen' : 'Cancel'}</Button><Button variant="destructive" onClick={handleDeleteUser}>{language === 'de' ? 'Löschen' : 'Delete'}</Button></DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Subscription Modal */}
        <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
          <DialogContent className="glass max-w-md">
            <DialogHeader><DialogTitle>{language === 'de' ? 'Abo-Plan ändern' : 'Change Subscription Plan'}</DialogTitle><DialogDescription>{language === 'de' ? `Abo-Plan für ${selectedUser?.email}` : `Subscription plan for ${selectedUser?.email}`}</DialogDescription></DialogHeader>
            <div className="py-4">
              <Label className="mb-3 block">{language === 'de' ? 'Neuer Plan' : 'New Plan'}</Label>
              <RadioGroup value={newPlan} onValueChange={setNewPlan} className="space-y-3">
                {[{ id: 'basic', label: 'Basic', desc: `€${pricing.basic?.price || 8.99}/Monat - 1 Account` }, { id: 'pro', label: 'Pro', desc: `€${pricing.pro?.price || 14.99}/Monat - 5 Accounts` }, { id: 'premium', label: 'Premium', desc: `€${pricing.premium?.price || 25.99}/Monat - Unbegrenzt` }].map(p => (
                  <div key={p.id} className="flex items-center space-x-3 p-3 rounded-lg border border-zinc-700 hover:border-primary transition-colors cursor-pointer">
                    <RadioGroupItem value={p.id} id={p.id} /><Label htmlFor={p.id} className="flex-1 cursor-pointer"><div className="font-semibold">{p.label}</div><div className="text-sm text-muted-foreground">{p.desc}</div></Label>
                  </div>
                ))}
              </RadioGroup>
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setShowEditModal(false)}>{language === 'de' ? 'Abbrechen' : 'Cancel'}</Button><Button onClick={handleUpdateSubscription}>{language === 'de' ? 'Speichern' : 'Save'}</Button></DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reset Password Modal */}
        <Dialog open={showResetModal} onOpenChange={setShowResetModal}>
          <DialogContent className="glass max-w-md">
            <DialogHeader><DialogTitle>{language === 'de' ? 'Passwort zurücksetzen' : 'Reset Password'}</DialogTitle><DialogDescription>{language === 'de' ? `Eine E-Mail mit einem Reset-Link wird an ${selectedUser?.email} gesendet.` : `An email with a reset link will be sent to ${selectedUser?.email}.`}</DialogDescription></DialogHeader>
            <DialogFooter><Button variant="outline" onClick={() => setShowResetModal(false)}>{language === 'de' ? 'Abbrechen' : 'Cancel'}</Button><Button onClick={handleResetPassword}><Mail className="h-4 w-4 mr-2" />{language === 'de' ? 'E-Mail senden' : 'Send Email'}</Button></DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Ticket Detail Modal */}
        <Dialog open={showTicketModal} onOpenChange={setShowTicketModal}>
          <DialogContent className="glass max-w-3xl max-h-[85vh] overflow-y-auto">
            {selectedTicket && (
              <>
                <DialogHeader>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-sm font-mono text-emerald-400">{selectedTicket.ticket_number}</span>
                    {getCategoryBadge(selectedTicket.category)}
                    {getStatusBadge(selectedTicket.status)}
                    <span className={`text-xs px-2 py-1 rounded-full ${getPlanBadgeColor(selectedTicket.subscription_plan)}`}>{selectedTicket.subscription_plan?.charAt(0).toUpperCase() + selectedTicket.subscription_plan?.slice(1)}</span>
                  </div>
                  <DialogTitle>{selectedTicket.title}</DialogTitle>
                  <DialogDescription>
                    Von: {selectedTicket.user_name} ({selectedTicket.user_email}) • {formatDate(selectedTicket.created_at)}
                  </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-4">
                  {/* Description */}
                  <div className="p-4 rounded-lg bg-zinc-800/50 border border-zinc-700">
                    <Label className="text-xs text-muted-foreground mb-2 block">Beschreibung</Label>
                    <p className="text-sm whitespace-pre-wrap">{selectedTicket.description}</p>
                  </div>

                  {/* Screenshot */}
                  {selectedTicket.screenshot_url && (
                    <div>
                      <Label className="mb-2 block">Screenshot</Label>
                      <img src={selectedTicket.screenshot_url} alt="Ticket screenshot" className="w-full rounded-lg border border-zinc-700" />
                    </div>
                  )}

                  {/* Attachments */}
                  {selectedTicket.attachments?.length > 0 && (
                    <div>
                      <Label className="mb-2 block flex items-center gap-2"><Image className="h-4 w-4" />Anhänge ({selectedTicket.attachments.length})</Label>
                      <div className="space-y-3">
                        {selectedTicket.attachments.map((att, idx) => (
                          <div key={idx} className="rounded-lg border border-zinc-700 overflow-hidden">
                            <div className="flex items-center justify-between p-2 bg-zinc-800/70 border-b border-zinc-700">
                              <span className="text-xs text-muted-foreground">{att.filename || `Anhang ${idx + 1}`}</span>
                              {att.data_url && (
                                <a href={att.data_url} download={att.filename || `anhang_${idx + 1}`} className="text-xs text-emerald-400 hover:underline">Download</a>
                              )}
                            </div>
                            {att.type === 'image' && att.data_url && (
                              <img src={att.data_url} alt={att.filename || `Anhang ${idx + 1}`} className="w-full max-h-[400px] object-contain" />
                            )}
                            {att.type === 'csv' && (
                              <div className="p-3 text-sm text-muted-foreground">CSV-Datei: {att.filename}</div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Status Update */}
                  <div className="p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                    <Label className="mb-2 block text-emerald-400">Status ändern</Label>
                    <div className="flex flex-wrap gap-2 mb-3">
                      {[{ value: 'open', label: 'Offen' }, { value: 'in_progress', label: 'In Bearbeitung' }, { value: 'done', label: 'Erledigt' }].map(s => {
                        const currentStatus = pendingStatus || selectedTicket.status;
                        const isSelected = currentStatus === s.value;
                        const hasChanged = pendingStatus && pendingStatus !== selectedTicket.status;
                        return (
                          <Button 
                            key={s.value} 
                            variant={isSelected ? 'default' : 'outline'} 
                            size="sm" 
                            onClick={() => handleUpdateTicketStatus(s.value)}
                            className={isSelected && hasChanged ? 'ring-2 ring-amber-400' : ''}
                          >
                            {s.label}
                            {isSelected && hasChanged && <span className="ml-1 text-amber-400">*</span>}
                          </Button>
                        );
                      })}
                    </div>
                    <Input value={statusNote} onChange={(e) => setStatusNote(e.target.value)} placeholder="Optionale Nachricht an den Kunden..." className="mb-2" />
                    <p className="text-xs text-muted-foreground">Diese Nachricht wird per E-Mail an den Kunden gesendet.</p>
                  </div>

                  {/* Comments */}
                  <div>
                    <Label className="mb-2 block flex items-center gap-2"><MessageSquare className="h-4 w-4" />Kommentare</Label>
                    <div className="space-y-3 max-h-[200px] overflow-y-auto mb-3">
                      {selectedTicket.comments?.length > 0 ? selectedTicket.comments.map((comment) => (
                        <div key={comment.comment_id} className={`p-3 rounded-lg ${comment.is_admin ? 'bg-emerald-500/10 border border-emerald-500/30' : 'bg-zinc-800/50 border border-zinc-700'}`}>
                          <div className="flex items-center justify-between mb-1">
                            <span className={`text-xs font-medium ${comment.is_admin ? 'text-emerald-400' : 'text-muted-foreground'}`}>{comment.user_name}{comment.is_admin && ' (Support)'}</span>
                            <span className="text-xs text-muted-foreground">{formatDate(comment.created_at)}</span>
                          </div>
                          <p className="text-sm">{comment.text}</p>
                        </div>
                      )) : <p className="text-sm text-muted-foreground text-center py-4">Noch keine Kommentare</p>}
                    </div>
                    <Input value={adminComment} onChange={(e) => setAdminComment(e.target.value)} placeholder="Admin-Kommentar hinzufügen..." className="mb-3" />
                  </div>

                  {/* Save Button */}
                  <div className="pt-4 border-t border-zinc-700">
                    <Button 
                      onClick={handleSaveTicketChanges} 
                      disabled={savingTicket || (!adminComment.trim() && !pendingStatus)}
                      className="w-full glow-primary"
                      data-testid="save-ticket-btn"
                    >
                      {savingTicket ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Wird gespeichert...
                        </>
                      ) : (
                        <>
                          <Save className="h-4 w-4 mr-2" />
                          Änderungen speichern & E-Mail senden
                        </>
                      )}
                    </Button>
                    {(pendingStatus || adminComment.trim()) && (
                      <p className="text-xs text-amber-400 text-center mt-2">
                        * Ungespeicherte Änderungen vorhanden
                      </p>
                    )}
                  </div>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>

      {/* Date Fix Migration Modal */}
      <Dialog open={showMigrateModal} onOpenChange={setShowMigrateModal}>
        <DialogContent className="bg-zinc-900 border-zinc-700 max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-orange-400" />
              Trade-Daten reparieren (DD/MM → MM/DD)
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Korrigiert Trades, die mit falschem Datumsformat importiert wurden (TopstepX US-Format).
              Tag und Monat werden getauscht.
            </p>
            <div>
              <Label>Account-ID</Label>
              <Input
                data-testid="migrate-account-id"
                value={migrationAccountId}
                onChange={(e) => setMigrationAccountId(e.target.value)}
                placeholder="acc_xxxxxx"
                className="bg-zinc-800 border-zinc-700 mt-1"
              />
            </div>
            <div className="flex gap-2">
              <Button
                data-testid="migrate-dry-run-btn"
                variant="outline"
                onClick={() => handleFixDates(true)}
                disabled={migrating}
                className="flex-1 border-blue-500/30 text-blue-400 hover:bg-blue-500/10"
              >
                {migrating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Eye className="h-4 w-4 mr-2" />}
                Vorschau (Dry Run)
              </Button>
              <Button
                data-testid="migrate-execute-btn"
                onClick={() => handleFixDates(false)}
                disabled={migrating || !migrationResult}
                className="flex-1 bg-orange-600 hover:bg-orange-700"
              >
                {migrating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                Migration ausführen
              </Button>
            </div>
            {migrationResult && (
              <div className="rounded-lg bg-zinc-800/50 border border-zinc-700 p-3 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Gesamt Trades:</span>
                  <span className="font-mono">{migrationResult.total_trades}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{migrationResult.dry_run ? 'Würden gefixt:' : 'Gefixt:'}</span>
                  <span className="font-mono text-emerald-400">{migrationResult.fixed}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Übersprungen:</span>
                  <span className="font-mono text-zinc-400">{migrationResult.skipped}</span>
                </div>
                {migrationResult.changes?.length > 0 && (
                  <div className="mt-2 pt-2 border-t border-zinc-700">
                    <p className="text-xs text-muted-foreground mb-1">Beispiel-Änderungen:</p>
                    <div className="max-h-40 overflow-y-auto space-y-1">
                      {migrationResult.changes.slice(0, 10).map((c, i) => (
                        <div key={i} className="text-xs font-mono flex gap-2">
                          <span className="text-zinc-400">{c.symbol}</span>
                          <span className="text-red-400">{c.bought_timestamp_old?.slice(0, 10)}</span>
                          <span className="text-zinc-500">→</span>
                          <span className="text-emerald-400">{c.bought_timestamp_new?.slice(0, 10)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {!migrationResult.dry_run && migrationResult.fixed > 0 && (
                  <p className="text-emerald-400 text-xs font-medium pt-1">Migration erfolgreich abgeschlossen!</p>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </TooltipProvider>
  );
};

export default AdminDashboardPage;
